<script>
   /*************************************************************/
   /* PlotDistance for PCA results                              */
   /* ----------------------------                              */
   /* shows plot with score and orthogonal distances            */
   /* and acceptance areas  (both for objects and variables)    */
   /*************************************************************/

   import { Lines, ColormapLegend } from 'svelte-plots-basic/2d';
   import { pcares_get } from '..//wasm/dist/pca.js';

   import { getColorGroups, getDistanceCoords, getDistanceAxisLabel,
            getDistanceLimitCoordinates, MAX_LABEL_SYMBOLS } from './Defaults.js';
   import PlotSeries from './PlotSeries.svelte';
   import Axes from './Axes.svelte';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   // get the parameters needed for the plot
   // get the parameters needed for the plot
   let data = $derived(props && props.data ? props.data : null);
   let res = $derived(props && props.res ? props.res : null);
   let resobj = $derived(params && params.resID ? res[params.resID] : null);
   let dataset = $derived(params && params.resID ? data[params.resID] : null);

   // constant plot parameters
   const LINE_COLOR = '#909090';    // color of the limits lines
   const NPOINTS = 100;             // number of points in limit lines



   // get specific type of limits
   let N = $derived(params.limType === 'classic' ?
      {h: resobj['cNh'], q: resobj['cNq']} :
      {h: resobj['rNh'], q: resobj['rNq']}
   );

   let u0 = $derived(params.limType === 'classic' ?
      {h: resobj['ch0'], q: resobj['cq0']} :
      {h: resobj['rh0'], q: resobj['rq0']}
   );

   // get limit values for specific number of components
   let Na = $derived({h: N.h[params.ncomp - 1], q: N.q[params.ncomp - 1]});
   let u0a = $derived({h: u0.h[params.ncomp - 1], q: u0.q[params.ncomp - 1]});

   // get conf limits
   let ecl = $derived(params.alpha ? 1 - params.alpha/100 : 0.95);
   let ocl = $derived(params.gamma ? 1 - params.gamma/100 : 0.99);

   // get coordinates for limits
   let els = $derived(getDistanceLimitCoordinates(ecl, Na.h, Na.q, params.transType));
   let ols = $derived(getDistanceLimitCoordinates(Math.pow(ocl, 1/resobj.nrows), Na.h, Na.q, params.transType));

   // reactive expression to compute colorby parameters
   let col = $derived(getColorGroups(dataset, resobj, params.colorBy, params.colorByVar, params.resID, params.colorByObj));
   let h = $derived(resobj ? pcares_get(resobj, "hdist", params.ncomp): null);
   let q = $derived(resobj ? pcares_get(resobj, "qdist", params.ncomp): null);

   // compute coordinates and labels
   let xc = $derived(getDistanceCoords(h, Na.h, u0a.h, params.transType));
   let yc = $derived(getDistanceCoords(q, Na.q, u0a.q, params.transType));
   let labels = $derived(resobj ? resobj.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null);

   // derive plot parameters
   let title = $derived(params.title ? params.title : 'Distances (A = ' + params.ncomp + ')');
   let xLabel = $derived(getDistanceAxisLabel('h', params.transType));
   let yLabel = $derived(getDistanceAxisLabel('q', params.transType));

   // ranges of values to define coordinate limits
   let xRange = $state(), yRange = $state();


   let limX = $derived.by(() => {
      if (!xc || !xRange) return [0, 1];
      const xMaxTot = Math.max(xc.max, ols.x.v[NPOINTS - 1]);
      return [-0.02 * (xMaxTot - xRange[0]), 1.05 * xMaxTot];
   });

   let limY = $derived.by(() => {
      if (!yc || !yRange) return [0, 1];
      const yMaxTot = Math.max(yc.max, ols.y.v[0]);
      return [-0.02 * (yMaxTot - yRange[0]), (1.05 + 0.10 * (params.colorBy !== 'none')) * yMaxTot];
   });
</script>

{#if xc && yc}
<Axes {title} {limX} {limY} {xLabel} {yLabel} fileName='plot-distance' onclick={callbacks.unselectObject}>

   <!-- limits  -->
   {#if els }
   <Lines xValues={els.x} yValues={els.y} lineColor={LINE_COLOR} lineType={2} />
   {/if}

   {#if ols}
   <Lines xValues={ols.x} yValues={ols.y} lineColor={LINE_COLOR} lineType={3} />
   {/if}

   <!-- data points  -->
   <PlotSeries
      {labels}
      {selected}
      {callbacks}
      xValues={xc.values}
      yValues={yc.values}
      plotType='p'
      showLabels={params.showLabels}
      colors={col.colors}
      bind:xRange={xRange}
      bind:yRange={yRange}
   />


   <!-- color by legend-->
   {#if col}
   <ColormapLegend show={col.breaks !== null} colmap={col.map} breaks={col.breaks} labels={col.labels}/>
   {/if}
</Axes>
{/if}
